import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockIcon from "@mui/icons-material/Lock";
import {
  type ButtonHTMLAttributes,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
} from "react";

import * as Styled from "./FilterChip.styles";
import { useFilterChipVariant } from "./FilterChipVariant";

export interface FilterChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: ReactNode;
  icon?: ReactNode;
  valueLabel?: ReactNode;
  defaultValueLabel?: ReactNode;
  open?: boolean;
  locked?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onClear?: () => void;
  ref?: Ref<HTMLButtonElement>;
}

export const FilterChip: FC<FilterChipProps> = ({
  label,
  icon,
  valueLabel,
  defaultValueLabel,
  open,
  locked,
  onClick,
  onClear,
  onKeyUp,
  ref,
  ...rest
}) => {
  const isToolbar = useFilterChipVariant() === "toolbar";
  const isPremium = locked !== undefined;
  const isApplied = valueLabel !== undefined && valueLabel !== null;
  const isClearable = isApplied && !locked && Boolean(onClear);
  const toolbarValue = locked ? undefined : (valueLabel ?? defaultValueLabel);

  const handleClear = (event: MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    onClear?.();
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLButtonElement>) => {
    onKeyUp?.(event);

    if (isClearable && (event.key === "Backspace" || event.key === "Delete")) {
      onClear?.();
    }
  };

  return (
    <Styled.ChipRoot
      {...rest}
      ref={ref}
      toolbar={isToolbar}
      active={!isToolbar && isApplied && !isPremium}
      gold={isPremium}
      onClick={onClick}
      onKeyUp={handleKeyUp}
    >
      {icon}
      <Styled.Label>
        {!isToolbar && isApplied && !locked ? valueLabel : label}
      </Styled.Label>
      {isToolbar && toolbarValue != null && (
        <Styled.Value>{toolbarValue}</Styled.Value>
      )}
      {locked && <LockIcon className="FilterChip-lockIcon" />}
      {isToolbar &&
        !locked &&
        open !== undefined &&
        (open ? (
          <ExpandLessIcon className="FilterChip-chevron" />
        ) : (
          <ExpandMoreIcon className="FilterChip-chevron" />
        ))}
      {!isToolbar && isClearable && (
        <CloseIcon className="FilterChip-clearIcon" onClick={handleClear} />
      )}
    </Styled.ChipRoot>
  );
};
